import {
  BROADCAST_AUDIO_CONSTRAINTS,
  BROADCAST_CAMERA_DEVICE_SELECTION_ALGORITHM_TYPE,
  BROADCAST_LAST_USED_CAMERA_DEVICE_ID_COOKIE_NAME,
  BROADCAST_LAST_USED_MICROPHONE_DEVICE_ID_COOKIE_NAME,
  BROADCAST_FACE_BLUR_ENABLED_COOKIE_NAME,
  BROADCAST_FACE_BLUR_FEATURE_VISIBLE_COOKIE_NAME,
  BROADCAST_FACE_BLUR_MODE_COOKIE_NAME,
  FaceBlurMode,
  BROADCAST_MAX_ALLOWED_BITRATE_IN_BPS,
  BROADCAST_MAX_ALLOWED_FRAME_RATE,
  BROADCAST_MAX_ALLOWED_RESOLUTION,
  BROADCAST_MICROPHONE_DEVICE_SELECTION_ALGORITHM_TYPE,
  BROADCAST_MOBILE_DEFAULT_VIDEO_BITRATE_KBPS,
  BROADCAST_MOBILE_DEFAULT_AUDIO_BITRATE_KBPS,
  BROADCAST_MOBILE_DEFAULT_MAX_FRAMERATE,
  WOWZA_DEFAULT_WEBRTC_SETTINGS,
} from "common/broadcast/_stores/broadcast-stream/consts";
import {
  BroadcastGatherMediaDeviceDataResponse,
  BroadcastOBSVideoSettings,
  BroadcastOBSVideoSettingsValidation,
  BroadcastPeerConnectionListeners,
  BroadcastVideoResolutionOption,
  BroadcastViewerCredentialsResponse,
  BroadcastWebRTCExtendedMediaDeviceInfo,
  BroadcastWebRTCPeerConnectionConfig,
  BroadcastWebRTCWebsocketListeners,
  NanoPlayerSettings,
  WowzaWebRTCStreamSettings,
} from "common/broadcast/_stores/broadcast-stream/types";
import { logger } from "library/core/utility";
import { BROADCAST_CAMERA_RESOLUTIONS } from "common/broadcast/_stores/broadcast/consts";
import CamsNanoPlayer from "core/utility/nano/nano";
import { api } from "core/utility";
import axios from "axios";
import config from "core/config";
import wowzaMungeSDP from "common/broadcast/_stores/broadcast-stream/WowzaMungeSDP";
import LayoutStore, {
  layoutStore,
} from "library/core/stores/layout/LayoutStore";
import { BroadcastDefaultDeviceSelectionAlgorithm } from "common/broadcast/_stores/broadcast-stream/enums";
import CookieStorage from "library/core/utility/cookie-storage";
import { broadcastStreamStore } from "core/stores";
import { parseResolutionString } from "common/broadcast/_stores/broadcast-stream/utils";

const logPrefix = "[BroadcastStreamService]:";

export class BroadcastStreamService {
  initializeWindowScope = () => {
    this.log("initializeWindowScope started");
    window.AudioContext =
      window.AudioContext || (window as any).webkitAudioContext || false;
    (window as any).DEFAULT_WOWZA_WEBRTC_SETTINGS =
      WOWZA_DEFAULT_WEBRTC_SETTINGS;
    this.log("initializeWindowScope finished");
  };

  getOBSSettingsValidation = (settings: BroadcastOBSVideoSettings | null) => {
    if (settings) {
      this.log("getOBSSettingsValidation started", settings);
      const { width: videoWidth, height: videoHeight } = parseResolutionString(
        settings?.resolution
      );
      const { width: maxVideoWidth, height: maxVideoHeight } =
        parseResolutionString(BROADCAST_MAX_ALLOWED_RESOLUTION);
      const hasLargerResolutionThanAllowed =
        videoHeight > maxVideoHeight || videoWidth > maxVideoWidth;
      const hasHigherFrameRateThanAllowed =
        settings?.frameRate > BROADCAST_MAX_ALLOWED_FRAME_RATE;
      const hasHigherBitRateThanAllowed =
        settings?.bitRate > BROADCAST_MAX_ALLOWED_BITRATE_IN_BPS;

      if (
        hasLargerResolutionThanAllowed ||
        hasHigherFrameRateThanAllowed ||
        hasHigherBitRateThanAllowed
      ) {
        this.log(
          "validateStreamStats received a video with higher width/height or frameRate or bitRate"
        );
        return {
          resolution: !hasLargerResolutionThanAllowed,
          frameRate: !hasHigherFrameRateThanAllowed,
          bitRate: !hasHigherBitRateThanAllowed,
        };
      }
    }

    // for WebRTC, we don't do checks so return all true
    return {
      resolution: true,
      frameRate: true,
      bitRate: true,
    };
  };

  getAreAllOBSSettingsValid = (
    settings: BroadcastOBSVideoSettingsValidation
  ) => {
    return Object.values(settings).every(s => s === true);
  };

  setMediaStreamTrackEnabled = (
    stream: MediaStream | null,
    trackKind: "video" | "audio",
    enabled: boolean
  ) => {
    this.log("setMediaStreamTrackEnabled started");
    if (stream != null && stream.getTracks != null) {
      stream.getTracks().map(track => {
        if (track.kind === trackKind) {
          track.enabled = enabled;
        }
      });
    }
    this.log("setMediaStreamTrackEnabled finished");
  };

  setSelectedCameraDevice = async (
    peerConnection: RTCPeerConnection | null,
    videoSender: RTCRtpSender | null,
    stream: MediaStream | null,
    cameraId: string
  ): Promise<{ stream: MediaStream; actualDeviceId: string }> => {
    this.log("setSelectedCameraDevice started");

    if (stream) {
      const previousAudioTrack = this.getMediaStreamAudioTracks(stream)[0];
      let newMediaStream: MediaStream | null = null;

      try {
        newMediaStream = await this.getUserMedia({
          audio: false,
          video: {
            deviceId: { exact: cameraId },
          },
        });
      } catch (error) {
        if (error.name === "OverconstrainedError") {
          this.log(
            `OverconstrainedError: Failed to getUserMedia with cameraId: ${cameraId}, constraint: ${error.constraintName}`
          );

          this.log("Falling back to default video constraints");

          try {
            newMediaStream = await this.getUserMedia({
              audio: false,
              video: true,
            });
          } catch (fallbackError) {
            this.log(
              `Fallback getUserMedia failed: ${
                fallbackError.message || fallbackError
              }`
            );

            if (
              fallbackError.name === "NotReadableError" ||
              (fallbackError.message &&
                (fallbackError.message.includes(
                  "Could not start video source"
                ) ||
                  fallbackError.message.includes("in use") ||
                  fallbackError.message.includes("is busy")))
            ) {
              this.log(
                "NotReadableError encountered in fallback, camera is likely in use"
              );

              const standardizedError = {
                name: "NotReadableError",
                message: "Camera is already in use by another application",
                originalError: {
                  name: fallbackError.name,
                  message: fallbackError.message,
                },
              };

              try {
                broadcastStreamStore.onErrorWebsocketForWebRTC(
                  standardizedError,
                  false
                );
              } catch (handlerError) {
                this.log("Error in error handler:", handlerError);
              }
            }

            throw fallbackError;
          }
        } else if (
          error.name === "NotReadableError" ||
          (error.message &&
            (error.message.includes("Could not start video source") ||
              error.message.includes("in use") ||
              error.message.includes("is busy")))
        ) {
          this.log(
            "NotReadableError encountered, camera is likely in use by another application"
          );

          const standardizedError = {
            name: "NotReadableError",
            message: "Camera is already in use by another application",
            originalError: {
              name: error.name,
              message: error.message,
            },
          };

          try {
            broadcastStreamStore.onErrorWebsocketForWebRTC(
              standardizedError,
              false
            );
          } catch (handlerError) {
            this.log("Error in error handler:", handlerError);
          }

          throw standardizedError;
        } else {
          this.log(`getUserMedia failed: ${error.message || error}`);
          throw error;
        }
      }

      try {
        const newVideoTrack = this.getMediaStreamVideoTracks(newMediaStream)[0];
        const updatedStream = new MediaStream();

        if (newVideoTrack) {
          updatedStream.addTrack(newVideoTrack);
        }

        if (previousAudioTrack) {
          updatedStream.addTrack(previousAudioTrack);
        }

        // Get the actual device ID from the track settings - this may differ from
        // the requested cameraId if there was a fallback due to OverconstrainedError
        const actualDeviceId =
          newVideoTrack?.getSettings()?.deviceId || cameraId;

        this.setLastUsedCameraDeviceIdToCookies(actualDeviceId);

        if (
          peerConnection &&
          videoSender &&
          videoSender.track &&
          newVideoTrack
        ) {
          videoSender.replaceTrack(newVideoTrack);
        }

        return { stream: updatedStream, actualDeviceId };
      } catch (trackError) {
        this.log(
          `Error handling media tracks: ${trackError.message || trackError}`
        );
        throw trackError;
      }
    }

    throw new Error("No media stream available");
  };

  replaceTrack = async (
    peerConnection: RTCPeerConnection | null,
    audioSender: RTCRtpSender | null,
    videoSender: RTCRtpSender | null,
    type: "audio" | "video",
    newTrack: MediaStreamTrack
  ) => {
    if (peerConnection != null) {
      if (type === "audio") {
        if (audioSender != null) {
          await audioSender.replaceTrack(newTrack);
        } else {
          audioSender = peerConnection.addTrack(newTrack);
        }
      } else if (type === "video") {
        if (videoSender != null) {
          this.log("Replacing track", newTrack);
          await videoSender.replaceTrack(newTrack);
        } else {
          this.log("Adding new track", newTrack);
          videoSender = peerConnection.addTrack(newTrack);
        }
      }
    }

    return {
      peerConnection,
      audioSender,
      videoSender,
    };
  };

  setSelectedMicrophoneDevice = async (
    peerConnection: RTCPeerConnection | null,
    audioSender: RTCRtpSender | null,
    videoSender: RTCRtpSender | null,
    stream: MediaStream | null,
    microphoneId: string
  ) => {
    const existingStreamVideoTracks = this.getMediaStreamVideoTracks(stream);
    if (stream) {
      this.log("setSelectedMicrophoneDevice started");
      const existingAudioTrack = this.getMediaStreamAudioTracks(stream)[0];
      stream = await this.getUserMedia({
        audio: {
          deviceId: microphoneId,
        },
      });
      this.setLastUsedMicrophoneDeviceToCookies(microphoneId);
      const tracks = broadcastStreamService.getMediaStreamAudioTracks(stream);
      if (tracks[0] && peerConnection && audioSender && videoSender) {
        await this.replaceTrack(
          peerConnection,
          audioSender,
          videoSender,
          "audio",
          tracks[0]
        );
        stream.removeTrack(existingAudioTrack);
      }
    }

    existingStreamVideoTracks.forEach(track => {
      stream?.addTrack(track);
    });

    this.log("setSelectedMicrophoneDevice finished, returning stream");
    return stream;
  };

  closePeerConnection = async (peerConnection: RTCPeerConnection | null) => {
    return new Promise<void>(resolve => {
      this.log("closePeerConnection started");
      if (peerConnection) {
        peerConnection.close();
      }

      resolve();
      this.log("closePeerConnection finished");
    });
  };

  closeWebsocketConnection = async (websocketConnection: WebSocket | null) => {
    return new Promise<void>(resolve => {
      this.log("closeWebsocketConnection started");
      if (websocketConnection) {
        websocketConnection.close();
      }
      resolve();
      this.log("closeWebsocketConnection finished");
    });
  };

  createWebsocketInstanceForWebRTC = (url: string): WebSocket | null => {
    this.log("connectToWebsocketsForWebRTC started");
    let connection: WebSocket | null = null;
    try {
      connection = new WebSocket(url);
      connection.binaryType = "arraybuffer";
      return connection;
    } catch (e) {
      this.log("connectToWebsocketsForWebRTC failed", e);
      return null;
    }
  };

  addWebsocketListeners = (
    connection: WebSocket | null,
    listeners: BroadcastWebRTCWebsocketListeners
  ): WebSocket | null => {
    if (connection) {
      Object.keys(listeners).forEach(key => {
        this.log("addWebsocketListeners adding listener", key);
        connection.addEventListener(key, listeners[key]);
      });
      return connection;
    } else {
      return null;
    }
  };

  createPeerConnectionInstanceForWebRTC = (
    config: BroadcastWebRTCPeerConnectionConfig
  ) => {
    const connection = new RTCPeerConnection(config);
    return connection;
  };

  addPeerConnectionListeners = (
    connection: RTCPeerConnection | null,
    listeners: BroadcastPeerConnectionListeners
  ) => {
    Object.keys(listeners).forEach(key => {
      connection?.addEventListener(key, listeners[key]);
    });
    return connection;
  };

  getAudioAndVideoSenderFromPeerConnection = (
    peerConnection: RTCPeerConnection | null,
    mediaStream: MediaStream | null
  ): { audioSender: RTCRtpSender | null; videoSender: RTCRtpSender | null } => {
    this.log("getAudioAndVideoSenderFromPeerConnection started");
    let audioSender: RTCRtpSender | null = null;
    let videoSender: RTCRtpSender | null = null;
    if (peerConnection && mediaStream) {
      const localTracks = mediaStream.getTracks();
      this.log(
        "getAudioAndVideoSenderFromPeerConnection found peerConnection and mediaStream, started adding local tracks to peerConnection"
      );
      for (const localTrack of localTracks) {
        const sender = peerConnection.addTrack(localTrack, mediaStream);
        this.log(
          "getAudioAndVideoSenderFromPeerConnection adding track",
          localTrack,
          "with kind",
          localTrack.kind
        );
        if (localTrack.kind === "audio") {
          audioSender = sender;
        } else if (localTrack.kind === "video") {
          videoSender = sender;
        }
      }
    } else {
      this.log(
        "getAudioAndVideoSenderFromPeerConnection could not find either peerConnection or mediaStream",
        peerConnection,
        mediaStream
      );
    }
    this.log("getAudioAndVideoSenderFromPeerConnection finished");
    return {
      audioSender,
      videoSender,
    };
  };

  /**
   * Set codec preferences on peer connection transceivers (alternative to SDP munging)
   * Should be called after tracks are added but before createOffer()
   */
  setMobileCodecPreferences = (peerConnection: RTCPeerConnection | null) => {
    if (!peerConnection) {
      this.log("setMobileCodecPreferences: no peer connection");
      return;
    }

    this.log("setMobileCodecPreferences started");

    try {
      const transceivers = peerConnection.getTransceivers();

      for (const transceiver of transceivers) {
        const kind = transceiver.sender.track?.kind;

        if (kind === "video") {
          const capabilities = RTCRtpReceiver.getCapabilities?.("video");
          if (capabilities?.codecs) {
            // For Wowza compatibility: ONLY allow H.264 Constrained Baseline Profile (42e01f)
            // Wowza WebRTC cannot handle High/Main profiles
            const h264Baseline = capabilities.codecs.filter(
              codec =>
                codec.mimeType === "video/H264" &&
                codec.sdpFmtpLine?.includes("profile-level-id=42e01f")
            );

            // Include RTX, RED, ULPFEC for error correction
            const rtxCodecs = capabilities.codecs.filter(
              codec => codec.mimeType === "video/rtx"
            );
            const redCodecs = capabilities.codecs.filter(
              codec => codec.mimeType === "video/red"
            );
            const ulpfecCodecs = capabilities.codecs.filter(
              codec => codec.mimeType === "video/ulpfec"
            );

            // Only include baseline H264 + error correction codecs
            // Exclude VP8, VP9, AV1, and other H264 profiles for Wowza compatibility
            const filteredCodecs = [
              ...h264Baseline,
              ...rtxCodecs,
              ...redCodecs,
              ...ulpfecCodecs,
            ];

            if (filteredCodecs.length > 0) {
              try {
                transceiver.setCodecPreferences(filteredCodecs);
                this.log(
                  "setMobileCodecPreferences: set video codec preferences (baseline H264 only)",
                  h264Baseline.map(c => c.sdpFmtpLine)
                );
              } catch (e) {
                this.log(
                  "setMobileCodecPreferences: failed to set video codec preferences, trying fallback",
                  e
                );
                // Fallback: try with all codecs but baseline first
                const h264Other = capabilities.codecs.filter(
                  codec =>
                    codec.mimeType === "video/H264" &&
                    !codec.sdpFmtpLine?.includes("profile-level-id=42e01f")
                );
                const otherCodecs = capabilities.codecs.filter(
                  codec => codec.mimeType !== "video/H264"
                );
                const fallbackCodecs = [
                  ...h264Baseline,
                  ...h264Other,
                  ...otherCodecs,
                ];
                try {
                  transceiver.setCodecPreferences(fallbackCodecs);
                  this.log(
                    "setMobileCodecPreferences: fallback succeeded with all codecs"
                  );
                } catch (e2) {
                  this.log(
                    "setMobileCodecPreferences: fallback also failed",
                    e2
                  );
                }
              }
            } else {
              this.log(
                "setMobileCodecPreferences: no H264 baseline codecs found, skipping"
              );
            }
          }
        } else if (kind === "audio") {
          const capabilities = RTCRtpReceiver.getCapabilities?.("audio");
          if (capabilities?.codecs) {
            // Prefer Opus
            const opus = capabilities.codecs.filter(
              codec => codec.mimeType === "audio/opus"
            );
            const otherCodecs = capabilities.codecs.filter(
              codec => codec.mimeType !== "audio/opus"
            );

            const orderedCodecs = [...opus, ...otherCodecs];

            try {
              transceiver.setCodecPreferences(orderedCodecs);
              this.log(
                "setMobileCodecPreferences: set audio codec preferences"
              );
            } catch (e) {
              this.log(
                "setMobileCodecPreferences: failed to set audio codec preferences",
                e
              );
            }
          }
        }
      }

      this.log("setMobileCodecPreferences finished");
    } catch (e) {
      this.log("setMobileCodecPreferences error", e);
    }
  };

  /**
   * Set bitrate constraints on senders (alternative to SDP munging)
   * Should be called after connection is established.
   * Mobile defaults are optimized for reduced CPU/GPU heat:
   * - 1500 kbps video (down from 2000) to lower encoder workload
   * - 24 fps (down from 30) for ~20% less encoding work per second
   * - "balanced" degradation preference so the encoder proportionally
   *   reduces both resolution and framerate when under thermal pressure
   */
  setMobileBitrateConstraints = async (
    peerConnection: RTCPeerConnection | null,
    videoBitrateKbps: number = BROADCAST_MOBILE_DEFAULT_VIDEO_BITRATE_KBPS,
    audioBitrateKbps: number = BROADCAST_MOBILE_DEFAULT_AUDIO_BITRATE_KBPS,
    maxFramerate: number = BROADCAST_MOBILE_DEFAULT_MAX_FRAMERATE
  ) => {
    if (!peerConnection) {
      this.log("setMobileBitrateConstraints: no peer connection");
      return;
    }

    this.log("setMobileBitrateConstraints started", {
      videoBitrateKbps,
      audioBitrateKbps,
      maxFramerate,
    });

    try {
      const senders = peerConnection.getSenders();

      for (const sender of senders) {
        const kind = sender.track?.kind;

        if (kind === "video") {
          try {
            const params = sender.getParameters() as RTCRtpSendParameters;

            if (!params.encodings || params.encodings.length === 0) {
              (params as any).encodings = [{}];
            }

            params.encodings[0].maxBitrate = videoBitrateKbps * 1000;
            (params.encodings[0] as any).maxFramerate = maxFramerate;
            (params as any).degradationPreference = "balanced";

            await sender.setParameters(params);
            this.log("setMobileBitrateConstraints: set video bitrate", {
              maxBitrate: videoBitrateKbps * 1000,
              maxFramerate,
              degradationPreference: "balanced",
            });
          } catch (e) {
            this.log(
              "setMobileBitrateConstraints: failed to set video parameters",
              e
            );
          }
        } else if (kind === "audio") {
          try {
            const params = sender.getParameters() as RTCRtpSendParameters;

            if (!params.encodings || params.encodings.length === 0) {
              (params as any).encodings = [{}];
            }

            params.encodings[0].maxBitrate = audioBitrateKbps * 1000;

            await sender.setParameters(params);
            this.log("setMobileBitrateConstraints: set audio bitrate", {
              maxBitrate: audioBitrateKbps * 1000,
            });
          } catch (e) {
            this.log(
              "setMobileBitrateConstraints: failed to set audio parameters",
              e
            );
          }
        }
      }

      this.log("setMobileBitrateConstraints finished");
    } catch (e) {
      this.log("setMobileBitrateConstraints error", e);
    }
  };

  createWebRTCOnStats(
    peerConnection: RTCPeerConnection,
    onStats: (report: RTCStatsReport) => void
  ) {
    return () => {
      if (peerConnection != null) {
        peerConnection.getStats(null).then(onStats, err => this.log(err));
      }
    };
  }

  createPeerNegotiationOffer = (
    peerConnection: RTCPeerConnection | null,
    websocketConnection: WebSocket | null,
    mungeData: any,
    webrtcSettings: WowzaWebRTCStreamSettings
  ) => {
    return new Promise<void>((resolve, reject) => {
      this.log("createPeerNegotiationOffer started");
      if (
        peerConnection &&
        websocketConnection &&
        websocketConnection.readyState > 0
      ) {
        peerConnection?.createOffer(
          (description: any) => {
            this.log(
              "createPeerNegotiationOffer success, starting peerConnection.setLocalDescription"
            );

            // Skip SDP munging for Chrome 127+ and mobile devices
            // (mobile browsers have stricter SDP parsing that can fail with codec errors)
            if (
              !LayoutStore.isChromeVersion127OrAbove() &&
              !layoutStore.isMobileDevice
            ) {
              description.sdp = wowzaMungeSDP(description.sdp, mungeData);
            }

            peerConnection
              .setLocalDescription(description)
              .then(() => {
                this.log(
                  "createPeerNegotiationOffer setLocalDescription success, sending websocket offer"
                );
                websocketConnection.send(
                  '{"direction":"publish", "command":"sendOffer", "streamInfo":' +
                    JSON.stringify(webrtcSettings.streamInfo) +
                    ', "sdp":' +
                    JSON.stringify(description) +
                    ', "userData":' +
                    JSON.stringify(webrtcSettings.userData) +
                    "}"
                );
                resolve();
                this.log(
                  "createPeerNegotiationOffer setLocalDescription finished"
                );
              })
              .catch(error => {
                reject(error);
              });
          },
          error => {
            reject(error);
          }
        );
      } else {
        reject();
      }
    });
  };

  setPeerConnectionRemoteDescription = async (
    peerConnection: RTCPeerConnection | null,
    sdpData: RTCSessionDescriptionInit
  ): Promise<RTCPeerConnection | null> => {
    return new Promise((resolve, reject) => {
      if (peerConnection) {
        peerConnection?.setRemoteDescription(
          new RTCSessionDescription(sdpData),
          () => {
            resolve(peerConnection);
          },
          error => {
            this.log("checkMessage setRemoteDescription failed", error);
            reject(error);
          }
        );
      } else {
        reject();
      }
    });
  };

  getIceCandidatesFromWebsocketResponse = (websocketResponse: any) => {
    this.log(
      "getIceCandidatesFromWebsocketResponse started",
      websocketResponse
    );
    this.log(
      "getIceCandidatesFromWebsocketResponse finished, returning iceCandidates"
    );
    return websocketResponse["iceCandidates"];
  };

  addIceCandidatesToPeerConnection = async (
    peerConnection: RTCPeerConnection | null,
    iceCandidates?: RTCIceCandidateInit[]
  ): Promise<RTCPeerConnection | null> => {
    return new Promise((resolve, reject) => {
      if (peerConnection) {
        if (iceCandidates !== undefined) {
          for (const index in iceCandidates) {
            this.log(
              "addIceCandidatesToPeerConnection adding ice candidate to peerConnection " +
                iceCandidates[index]
            );
            const candidateStr = JSON.stringify(iceCandidates[index]);
            const isFirefox = LayoutStore.isFirefoxUA();

            // If Firefox, only add UDP candidates, otherwise add all
            if ((isFirefox && candidateStr.indexOf("UDP") > 0) || !isFirefox) {
              peerConnection.addIceCandidate(
                new RTCIceCandidate(iceCandidates[index])
              );
            } else {
              this.log(
                "Skipping candidate due to browser compatibility: " +
                  candidateStr
              );
            }
          }
          resolve(peerConnection);
        } else {
          this.log(
            "addIceCandidatesToPeerConnection failed because iceCandidates were undefined"
          );
          reject();
        }
      } else {
        this.log(
          "addIceCandidatesToPeerConnection failed because there was no peerConnection"
        );
        reject();
      }
    });
  };

  getSDPDataFromWebsocketResponse = (
    websocketResponse: any
  ): RTCSessionDescriptionInit | undefined => {
    const sdpData = websocketResponse["sdp"];
    this.log("getSDPDataFromWebsocketResponse received SDP data", sdpData.sdp);
    return sdpData;
  };

  applyMediaStreamConstraints = async (
    stream: MediaStream | null,
    constraints: MediaStreamConstraints
  ) => {
    this.log("applyMediaStreamConstraints started");
    if (!layoutStore.isFirefox) {
      const promises: Promise<any>[] = [];
      if (stream) {
        const audioTracks =
          broadcastStreamService.getMediaStreamAudioTracks(stream);
        const videoTracks =
          broadcastStreamService.getMediaStreamVideoTracks(stream);
        for (const a in audioTracks) {
          promises.push(
            audioTracks[a].applyConstraints(
              constraints.audio as MediaTrackConstraints
            )
          );
        }
        for (const v in videoTracks) {
          promises.push(
            videoTracks[v].applyConstraints(
              constraints.video as MediaTrackConstraints
            )
          );
        }
      }
      await Promise.all(promises);
    }
    this.log("applyMediaStreamConstraints finished, returning stream");
    return stream;
  };

  getMediaStreamAudioTracks = (
    stream: MediaStream | null
  ): MediaStreamTrack[] => {
    this.log("getMediaStreamAudioTracks started");
    const tracks = stream ? stream.getAudioTracks() : [];
    this.log("getMediaStreamAudioTracks finished, returning tracks");
    return tracks;
  };

  getMediaStreamVideoTracks = (
    stream: MediaStream | null
  ): MediaStreamTrack[] => {
    this.log("getMediaStreamVideoTracks started");
    const tracks = stream ? stream.getVideoTracks() : [];
    this.log("getMediaStreamVideoTracks finished, returning tracks");
    return tracks;
  };

  log = (...params: any[]) => {
    logger.log(logPrefix, ...params);
  };

  getUserMedia = async (
    constraints: MediaStreamConstraints
  ): Promise<MediaStream> => {
    const fallbackConstraints: MediaStreamConstraints[] =
      BROADCAST_CAMERA_RESOLUTIONS.map(res => {
        const [width, height] = res.value.split("x").map(Number);
        return {
          audio: BROADCAST_AUDIO_CONSTRAINTS,
          video: { width, height },
        };
      });

    let currentAttempt = 0;

    const tryGetUserMedia = async (
      currentConstraints: MediaStreamConstraints
    ): Promise<MediaStream> => {
      return new Promise<MediaStream>((resolve, reject) => {
        this.log("Trying getUserMedia with constraints:", currentConstraints);

        const handleNotReadableError = (error: MediaStreamError | any) => {
          const standardizedError = {
            name: "NotReadableError",
            message: "Camera is already in use by another application",
            originalError: {
              name: error.name,
              message: error.message,
            },
          };
          this.log(
            "NotReadableError encountered, camera is already in use by another application"
          );

          try {
            broadcastStreamStore.onErrorWebsocketForWebRTC(
              standardizedError,
              false
            );
          } catch (handlerError) {
            this.log("Error in error handler:", handlerError);
          }

          reject(standardizedError);
        };

        if (navigator.mediaDevices && navigator.mediaDevices.getUserMedia) {
          navigator.mediaDevices
            .getUserMedia(currentConstraints)
            .then((_stream: MediaStream) => {
              resolve(_stream);
            })
            .catch(async (error: MediaStreamError) => {
              if (error.name === "OverconstrainedError") {
                this.log(
                  `OverConstrainedError encountered, constraint: ${error.constraintName}`
                );

                currentAttempt++;
                if (currentAttempt < fallbackConstraints.length) {
                  this.log(
                    `Retrying with fallback constraints #${currentAttempt + 1}`
                  );
                  const nextConstraints = fallbackConstraints[currentAttempt];
                  try {
                    resolve(await tryGetUserMedia(nextConstraints));
                  } catch (retryError) {
                    reject(retryError);
                  }
                } else {
                  this.log("No more fallback constraints, rejecting.");
                  reject(error);
                }
              } else if (
                error.name === "NotReadableError" ||
                (error.message &&
                  (error.message.includes("Could not start video source") ||
                    error.message.includes("in use") ||
                    error.message.includes("is busy")))
              ) {
                handleNotReadableError(error);
              } else {
                try {
                  const compatibleError = {
                    name: error.name,
                    message: error.message,
                    constraintName: error.constraintName,
                  };
                  broadcastStreamStore.onErrorWebsocketForWebRTC(
                    compatibleError,
                    false
                  );
                } catch (handlerError) {
                  this.log("Error in error handler:", handlerError);
                }
                reject(error);
              }
            });
        } else if (navigator.getUserMedia) {
          navigator.getUserMedia(
            currentConstraints,
            (_stream: MediaStream) => {
              try {
                resolve(_stream);
              } catch (error) {
                try {
                  const compatibleError = {
                    name: error.name,
                    message: error.message,
                    constraintName: error.constraintName,
                  };
                  broadcastStreamStore.onErrorWebsocketForWebRTC(
                    compatibleError,
                    false
                  );
                } catch (handlerError) {
                  this.log("Error in error handler:", handlerError);
                }
                reject(error);
              }
            },
            async (error: MediaStreamError) => {
              if (error.name === "OverconstrainedError") {
                this.log(
                  `OverConstrainedError encountered, constraint: ${error.constraintName}`
                );
                currentAttempt++;
                if (currentAttempt < fallbackConstraints.length) {
                  this.log(
                    `Retrying with fallback constraints #${currentAttempt + 1}`
                  );
                  const nextConstraints = fallbackConstraints[currentAttempt];
                  try {
                    resolve(await tryGetUserMedia(nextConstraints));
                  } catch (retryError) {
                    reject(retryError);
                  }
                } else {
                  this.log("No more fallback constraints, rejecting.");
                  reject(error);
                }
              } else if (
                error.name === "NotReadableError" ||
                (error.message &&
                  (error.message.includes("Could not start video source") ||
                    error.message.includes("in use") ||
                    error.message.includes("is busy")))
              ) {
                handleNotReadableError(error);
              } else {
                try {
                  const compatibleError = {
                    name: error.name,
                    message: error.message,
                    constraintName: error.constraintName,
                  };
                  broadcastStreamStore.onErrorWebsocketForWebRTC(
                    compatibleError,
                    false
                  );
                } catch (handlerError) {
                  this.log("Error in error handler:", handlerError);
                }
                reject(error);
              }
            }
          );
        } else {
          const browserError = {
            name: "NotSupportedError",
            message: "Your browser does not support WebRTC",
          };
          try {
            const compatibleError = {
              name: browserError.name,
              message: browserError.message,
              constraintName: "",
            };
            broadcastStreamStore.onErrorWebsocketForWebRTC(
              compatibleError,
              false
            );
          } catch (handlerError) {
            this.log("Error in error handler:", handlerError);
          }
          this.log("getUserMedia failed, does not support WebRTC");
          reject(browserError);
        }
      });
    };

    try {
      return await tryGetUserMedia(constraints);
    } catch (error) {
      this.log("Final error catch in getUserMedia:", error);
      throw error;
    }
  };

  stopMediaStreamTracks = (
    stream: MediaStream | null,
    trackTypeToStop: "all" | "video" | "audio" = "all"
  ) => {
    this.log("stopMediaStreamTracks started");
    if (stream) {
      const tracks = stream.getTracks();
      tracks.forEach(track => {
        if (
          trackTypeToStop === "all" ||
          (trackTypeToStop === "video" && track.kind === "video") ||
          (trackTypeToStop === "audio" && track.kind === "audio")
        ) {
          track.stop();
        }
      });
    }
    this.log("stopMediaStreamTracks finished");
  };

  getDoesCameraSupportResolution = async (
    resolutionString: string,
    deviceId: string
  ): Promise<boolean> => {
    try {
      this.log("getDoesCameraSupportResolution started for", resolutionString);
      const { width, height } = parseResolutionString(resolutionString);
      const stream = await this.getUserMedia({
        video: {
          width: { exact: width },
          height: { exact: height },
          deviceId: { exact: deviceId },
        },
      });
      const tracks = this.getMediaStreamVideoTracks(stream);

      let canSupport: boolean = false;

      tracks.forEach(track => {
        if (track.getCapabilities) {
          const capabilities = track.getCapabilities();
          if (capabilities.deviceId) {
            canSupport = true;
          }
        } else {
          const settings = track.getSettings();
          if (
            settings.deviceId &&
            settings.width === width &&
            settings.height === height
          ) {
            canSupport = true;
          }
        }
      });

      this.log(
        "getDoesCameraSupportResolution success for",
        resolutionString,
        deviceId
      );
      this.stopMediaStreamTracks(stream);
      return canSupport;
    } catch (err) {
      this.log(
        "getDoesCameraSupportResolution failed for",
        resolutionString,
        deviceId,
        "with error",
        err
      );
      if (
        err.name !== "OverconstrainedError" &&
        err.name !== "NotReadableError"
      ) {
        throw new Error(err.toString());
      } else {
        return false;
      }
    } finally {
      this.log("getDoesCameraSupportResolution finished");
    }
  };

  getAvailableResolutionsForCamera = async (cameraId: string) => {
    const resolutions: BroadcastVideoResolutionOption[] = [];
    for await (const resolutionObj of BROADCAST_CAMERA_RESOLUTIONS) {
      const doesSupport = await this.getDoesCameraSupportResolution(
        resolutionObj.value,
        cameraId
      );
      if (doesSupport) {
        resolutions.push(resolutionObj);
      }
    }

    return resolutions;
  };

  getMaxResolutionCameraDeviceSupports = (
    resolutions: BroadcastVideoResolutionOption[]
  ) => {
    this.log("getMaxResolutionCameraDeviceSupports started");
    let maxResolution: string = "1280x720";
    maxResolution = resolutions.reduce((maxResolution, resolution) => {
      const { width: widthOnMaxResolution } =
        parseResolutionString(maxResolution);
      const { width } = parseResolutionString(resolution.value);
      if (width > widthOnMaxResolution) {
        return resolution.value;
      }
      return maxResolution;
    }, maxResolution);

    this.log(
      "getMaxResolutionCameraDeviceSupports finished, returning maxResolution",
      maxResolution
    );
    return maxResolution;
  };

  gatherMediaDeviceDataForQAPurposes = async () => {
    const cameras: BroadcastWebRTCExtendedMediaDeviceInfo[] = [];
    const microphones: BroadcastWebRTCExtendedMediaDeviceInfo[] = [];
    try {
      const devices = await navigator.mediaDevices?.enumerateDevices();
      devices.forEach(device => {
        if (device.kind === "videoinput") {
          // spread syntax does not work for device therefore use Object.assign
          const extendedDevice = Object.assign(device, {
            ...device,
            canBeUsed: true,
          });
          cameras.push(extendedDevice);
        } else if (device.kind === "audioinput") {
          const extendedDevice = Object.assign(device, {
            ...device,
            canBeUsed: true,
          });
          microphones.push(extendedDevice);
        }
      });
    } catch (error) {}

    return {
      cameras,
      microphones,
    };
  };

  getLastUsedCameraDeviceIdFromCookies = (): string | undefined => {
    return CookieStorage.get(BROADCAST_LAST_USED_CAMERA_DEVICE_ID_COOKIE_NAME);
  };

  resetLastUsedCameraDeviceIdCookie = (): void => {
    CookieStorage.remove(BROADCAST_LAST_USED_CAMERA_DEVICE_ID_COOKIE_NAME);
  };

  setLastUsedCameraDeviceIdToCookies = (deviceId: string) => {
    this.log("setLastUsedCameraDeviceIdToCookies started", deviceId);
    const tenYearsAfterToday = new Date();
    tenYearsAfterToday.setFullYear(new Date().getFullYear() + 10);
    CookieStorage.set(
      BROADCAST_LAST_USED_CAMERA_DEVICE_ID_COOKIE_NAME,
      deviceId,
      {
        expires: tenYearsAfterToday,
      }
    );
    this.log("setLastUsedCameraDeviceIdToCookies finished");
  };

  getLastUsedMicrophoneDeviceIdFromCookies = (): string | undefined => {
    return CookieStorage.get(
      BROADCAST_LAST_USED_MICROPHONE_DEVICE_ID_COOKIE_NAME
    );
  };

  setLastUsedMicrophoneDeviceToCookies = (deviceId: string) => {
    this.log("setLastUsedMicrophoneDeviceToCookies started", deviceId);
    const tenYearsAfterToday = new Date();
    tenYearsAfterToday.setFullYear(new Date().getFullYear() + 10);
    CookieStorage.set(
      BROADCAST_LAST_USED_MICROPHONE_DEVICE_ID_COOKIE_NAME,
      deviceId,
      {
        expires: tenYearsAfterToday,
      }
    );
    this.log("setLastUsedMicrophoneDeviceToCookies finished");
  };

  /**
   * Get face blur feature mode from QA tool cookie.
   * Returns "mobile", "all", or "" (off).
   * Legacy boolean "true" values are treated as "mobile".
   */
  getFaceBlurFeatureMode = (): string => {
    const value = CookieStorage.get(
      BROADCAST_FACE_BLUR_FEATURE_VISIBLE_COOKIE_NAME
    );
    if (!value) return "";
    const strValue = String(value);
    // Legacy: old boolean cookie stored "true"
    return strValue === "true" ? "mobile" : strValue;
  };

  /**
   * Check if face blur feature is visible via QA tool (feature flag)
   * This controls whether the feature is available at all
   */
  isFaceBlurFeatureVisible = (): boolean => {
    return this.getFaceBlurFeatureMode() !== "";
  };

  /**
   * Get face blur enabled preference from cookie (user preference)
   * Returns false by default if no preference is saved
   */
  getFaceBlurEnabledFromStorage = (): boolean => {
    const value = CookieStorage.get(BROADCAST_FACE_BLUR_ENABLED_COOKIE_NAME);
    return !!value;
  };

  /**
   * Save face blur enabled preference to cookie (user preference)
   */
  setFaceBlurEnabledToCookies = (enabled: boolean) => {
    this.log("setFaceBlurEnabledToCookies started", enabled);
    CookieStorage.set(BROADCAST_FACE_BLUR_ENABLED_COOKIE_NAME, enabled, {
      sameSite: "none",
      secure: true,
    });
    this.log("setFaceBlurEnabledToCookies finished");
  };

  getFaceBlurModeFromStorage = (): FaceBlurMode => {
    const value = CookieStorage.get(BROADCAST_FACE_BLUR_MODE_COOKIE_NAME);
    return value === "server" ? "server" : "client";
  };

  setFaceBlurModeToCookies = (mode: FaceBlurMode) => {
    this.log("setFaceBlurModeToCookies started", mode);
    CookieStorage.set(BROADCAST_FACE_BLUR_MODE_COOKIE_NAME, mode, {
      sameSite: "none",
      secure: true,
    });
    this.log("setFaceBlurModeToCookies finished");
  };

  gatherMediaDeviceData = async (
    excludedDevices: BroadcastWebRTCExtendedMediaDeviceInfo[]
  ): Promise<BroadcastGatherMediaDeviceDataResponse> => {
    this.log("gatherMediaDeviceData started");
    const cameras: BroadcastWebRTCExtendedMediaDeviceInfo[] = [];
    const microphones: BroadcastWebRTCExtendedMediaDeviceInfo[] = [];
    let selectedCameraDeviceId,
      selectedMicrophoneDeviceId,
      maxResolution,
      resolutions;

    try {
      if (layoutStore.isFirefox) {
        await navigator.mediaDevices.getUserMedia({ audio: true, video: true });
      }

      const devices = await navigator.mediaDevices?.enumerateDevices();
      const filteredDevices = devices?.filter(device => {
        const excludedDevice = excludedDevices.find(
          _device => _device.deviceId === device.deviceId
        );
        return !excludedDevice;
      });

      const lastUsedCameraDeviceId =
        this.getLastUsedCameraDeviceIdFromCookies();
      const lastUsedMicrophoneDeviceId =
        this.getLastUsedMicrophoneDeviceIdFromCookies();

      const isLastUsedCameraDeviceIdValid = filteredDevices.some(
        device => device.deviceId === lastUsedCameraDeviceId
      );

      if (!isLastUsedCameraDeviceIdValid) {
        this.log("Invalid lastUsedCameraDeviceId, resetting...");
        selectedCameraDeviceId = null;
        this.resetLastUsedCameraDeviceIdCookie();
      }

      for await (const device of filteredDevices) {
        if (device.kind === "videoinput") {
          const resolutionsDeviceSupport =
            await this.getAvailableResolutionsForCamera(device.deviceId);

          const extendedDevice = Object.assign(device, {
            canBeUsed: resolutionsDeviceSupport.length > 0,
          });
          cameras.push(extendedDevice);

          if (resolutionsDeviceSupport.length) {
            const maxResolutionCameraDeviceSupports =
              this.getMaxResolutionCameraDeviceSupports(
                resolutionsDeviceSupport
              );

            const parsedMaxResolution =
              maxResolution !== undefined
                ? parseResolutionString(maxResolution)
                : undefined;
            const parsedSupportedResolution = parseResolutionString(
              maxResolutionCameraDeviceSupports
            );

            const hasCameraBetterResolutionThanCurrentMaxResolution =
              !maxResolution ||
              (parsedMaxResolution &&
                parsedSupportedResolution.width > parsedMaxResolution.width);

            if (hasCameraBetterResolutionThanCurrentMaxResolution) {
              maxResolution = maxResolutionCameraDeviceSupports;
            }

            if (
              (BROADCAST_CAMERA_DEVICE_SELECTION_ALGORITHM_TYPE ===
                BroadcastDefaultDeviceSelectionAlgorithm.BEST_AVAILABLE &&
                hasCameraBetterResolutionThanCurrentMaxResolution) ||
              (BROADCAST_CAMERA_DEVICE_SELECTION_ALGORITHM_TYPE ===
                BroadcastDefaultDeviceSelectionAlgorithm.LAST_USED &&
                (device.deviceId === lastUsedCameraDeviceId ||
                  !lastUsedCameraDeviceId ||
                  lastUsedCameraDeviceId === "undefined"))
            ) {
              selectedCameraDeviceId = device.deviceId;
            }

            if (!resolutions) {
              resolutions = [];
            }

            resolutionsDeviceSupport.forEach(resolution => {
              const existingResolution = resolutions?.find(
                _resolution => _resolution.value === resolution.value
              );
              if (existingResolution) {
                existingResolution.deviceIds.push(device.deviceId);
              } else {
                resolutions.push({
                  ...resolution,
                  deviceIds: [device.deviceId],
                });
              }
            });
          }
        } else if (device.kind === "audioinput") {
          const extendedDevice = Object.assign(device, { canBeUsed: true });
          microphones.push(extendedDevice);

          if (
            BROADCAST_MICROPHONE_DEVICE_SELECTION_ALGORITHM_TYPE ===
              BroadcastDefaultDeviceSelectionAlgorithm.FIRST_AVAILABLE ||
            (BROADCAST_MICROPHONE_DEVICE_SELECTION_ALGORITHM_TYPE ===
              BroadcastDefaultDeviceSelectionAlgorithm.LAST_USED &&
              (lastUsedMicrophoneDeviceId === device.deviceId ||
                !lastUsedMicrophoneDeviceId ||
                lastUsedMicrophoneDeviceId === "undefined"))
          ) {
            selectedMicrophoneDeviceId = device.deviceId;
          }
        }
      }

      if (!selectedCameraDeviceId) {
        this.log(
          "No valid camera selected based on cookie, falling back to default"
        );

        // On mobile devices, prefer the front-facing (user) camera for selfie mode
        if (layoutStore.isMobileDevice && cameras.length > 1) {
          try {
            // Use facingMode constraint to reliably get front camera
            const frontStream = await navigator.mediaDevices.getUserMedia({
              video: { facingMode: "user" },
            });
            const frontTrack = frontStream.getVideoTracks()[0];
            const frontDeviceId = frontTrack.getSettings().deviceId;
            frontTrack.stop();

            // Verify this camera is in our usable cameras list
            const frontCamera = cameras.find(
              device => device.deviceId === frontDeviceId && device.canBeUsed
            );

            if (frontCamera) {
              this.log(
                "Mobile device detected, selecting front-facing camera via facingMode"
              );
              selectedCameraDeviceId = frontCamera.deviceId;
            }
          } catch (error) {
            this.log(
              "Failed to detect front-facing camera via facingMode",
              error
            );
          }
        }

        // If still no camera selected, fall back to first available
        if (!selectedCameraDeviceId) {
          const firstAvailableCamera = cameras.find(device => device.canBeUsed);
          if (firstAvailableCamera) {
            selectedCameraDeviceId = firstAvailableCamera.deviceId;
          } else {
            this.log("No available cameras found");
          }
        }
      }

      this.log("gatherMediaDeviceData success");
    } catch (err) {
      this.log("gatherMediaDeviceData failed", err);
      throw new Error(err);
    } finally {
      this.log("gatherMediaDeviceData finished");
    }

    return {
      resolutions,
      maxResolution,
      cameras,
      microphones,
      selectedCameraDeviceId,
      selectedMicrophoneDeviceId,
    };
  };

  getMediaStreamForParticularDeviceId = async (
    deviceType: "audio" | "video",
    deviceId: string
  ): Promise<MediaStream> => {
    return new Promise((resolve, reject) => {
      this.getUserMedia({
        [deviceType]: {
          advanced: [
            {
              deviceId,
            },
          ],
        },
      })
        .then(stream => {
          resolve(stream);
        })
        .catch(reject);
    });
  };

  stopCamerasAndMicrophones = async (
    cameras: MediaDeviceInfo[],
    microphones: MediaDeviceInfo[]
  ) => {
    for await (const camera of cameras) {
      const stream = await this.getMediaStreamForParticularDeviceId(
        "video",
        camera.deviceId
      );
      this.stopMediaStreamTracks(stream);
    }
    for await (const microphone of microphones) {
      const stream = await this.getMediaStreamForParticularDeviceId(
        "audio",
        microphone.deviceId
      );
      this.stopMediaStreamTracks(stream);
    }
  };

  getMediaStreamWithAllMediaDevices = async (): Promise<MediaStream> => {
    return new Promise((resolve, reject) => {
      this.getUserMedia({
        video: true,
        audio: BROADCAST_AUDIO_CONSTRAINTS,
      })
        .then(stream => {
          this.log(
            "getAllMediaDevicesAndThenStopMediaStreamTracks success",
            stream.getTracks()
          );
          resolve(stream);
        })
        .catch(error => {
          reject(error);
        })
        .finally(() => {
          this.log("getAllMediaDevicesAndThenStopMediaStreamTracks finished");
        });
    });
  };

  getAdminCredentials = async (
    adminName: string,
    channelName: string,
    modelProfileId: string
  ) => {
    try {
      this.log("getAdminCredentials started");
      const { data } = await api.broadcastAdmins.get({
        pathParams: `${adminName}/${channelName}/${modelProfileId}`,
      });
      this.log("getAdminCredentials success", data);
      return data;
    } catch (error) {
      this.log("getAdminCredentials failed", error);
    }
  };

  getViewerCredentials = async (
    modelName: string,
    channelName: string,
    modelProfileId: string
  ): Promise<BroadcastViewerCredentialsResponse | undefined> => {
    try {
      this.log("getViewerCredentials started");
      const { data } = await api.broadcastViewers.get({
        pathParams: `${modelName?.toLowerCase()}/${channelName?.toLowerCase()}/${modelProfileId}`,
      });
      this.log("getViewerCredentials success", data);
      return data;
    } catch (error) {
      this.log("getViewerCredentials failed", error);
    }
  };

  getEdgeLBPath = async () => {
    try {
      this.log("getEdgeLBPath started");
      const { data: edgeLBPath } = await axios.get(config.edgeLBPath);
      return edgeLBPath;
    } catch (error) {
      this.log("getEdgeLBPath failed", error);
    } finally {
      this.log("getEdgeLBPath finished");
    }
  };

  initializeNanoAndPlayStream = async (
    hostId: string,
    settings: NanoPlayerSettings
  ): Promise<CamsNanoPlayer> => {
    return new Promise((resolve, reject) => {
      this.log("initializeNanoAndPlayStream started");
      try {
        const player = new CamsNanoPlayer({
          hostId,
        });
        player
          .initAndPlay(settings)
          .then(() => {
            resolve(player);
          })
          .catch(err => {
            this.log("initializeNanoAndPlayStream failed", err);
            reject(err);
          });
      } catch (err) {
        this.log("initializeNanoAndPlayStream failed", err);
        reject();
      }
    });
  };

  stopNanoPlayStream = async (player: CamsNanoPlayer | null) => {
    this.log("stopNanoPlayStream started");
    if (player) {
      player.stop();
    }
    this.log("stopNanoPlayStream finished");
  };

  getPublishToken = async (
    channelName: string,
    modelProfileId: string,
    hideError: boolean = false,
    retries: number = 0
  ): Promise<string | null> => {
    try {
      this.log("getPublishToken started");
      const {
        data: { token },
      } = await api.broadcastPublishers.get({
        pathParams: `${channelName}/${channelName}/${modelProfileId}`,
      });
      if (!token) {
        if (retries < 3) {
          //if we dont get a token, wait a second and try again
          return new Promise(resolve => setTimeout(resolve, 1000)).then(() => {
            return this.getPublishToken(
              channelName,
              modelProfileId,
              hideError,
              ++retries
            );
          });
        } else {
          throw new Error("Could not get publish token!");
        }
      } else {
        this.log("getPublishToken success");
        return token;
      }
    } catch (err) {
      this.log("getPublishToken failed, retrying", err);
      if (retries < 3) {
        return new Promise(resolve => setTimeout(resolve, 1000)).then(() => {
          return this.getPublishToken(
            channelName,
            modelProfileId,
            hideError,
            ++retries
          );
        });
      } else {
        if (!hideError) {
          broadcastStreamStore.setBroadcastError({
            id: "broadcast-error.general-error",
            defaultMessage:
              "There seems to be something wrong. Please try again",
          });
        }

        return null;
      }
    }
  };
}

export const broadcastStreamService = new BroadcastStreamService();
